import { useCallback } from 'react';
import { useInstantSearch, useRange } from 'react-instantsearch-hooks-web';
import { classnames } from 'tailwindcss-classnames';
import { FacetStateType } from '.';
import DatePicker from 'react-datepicker';
import { useSearchDictionary } from '../util';

type DateSearchFacetProps = {
  label: string;
  dateField: string;
  horizontal?: boolean;
  // stacked?: boolean; /*  Note: stacked is not being used, commented out */
  startFieldName: 'eventStartDate' | 'articleStartDate';
  endFieldName: 'eventEndDate' | 'articleEndDate';
  disallowDateRange: boolean;
};

const DateRangeFacet = ({
  label,
  dateField,
  horizontal,
  startFieldName,
  endFieldName,
  disallowDateRange,
}: DateSearchFacetProps) => {
  const dict = useSearchDictionary();

  const { indexUiState, setIndexUiState } = useInstantSearch();

  const dateFieldValue = indexUiState.range ? indexUiState.range[dateField] : undefined;

  const [startNumString, endNumString] = dateFieldValue?.split(':') || [undefined, undefined];

  const [start, end] = [
    startNumString ? new Date(parseInt(startNumString, 10)) : undefined,
    endNumString ? new Date(parseInt(endNumString, 10)) : undefined,
  ];

  const { range } = useRange({
    attribute: dateField,
  });

  // Begining of the start date
  const min = disallowDateRange ? new Date() : new Date(range.min || 0);
  min.setHours(0, 0, 0, 0);

  // End of the end date
  const max = new Date(range.max || Infinity);
  max.setHours(23, 59, 59, 999);
  const handleStateChange = useCallback(
    (fs: FacetStateType) => {
      const newRange: [number | undefined, number | undefined] = [
        ((fs[startFieldName] as Date) || start)?.setHours(0, 0, 0, 0),
        ((fs[endFieldName] as Date) || end)?.setHours(23, 59, 59, 999),
      ];

      // Refine was behaving weirdly with other components, so calling setIndexUiState directly.
      setIndexUiState((prev) => {
        return {
          ...prev,
          range: {
            ...prev.range,
            [dateField]: `${newRange[0]}: ${newRange[1]}`,
          },
        };
      });
    },
    [startFieldName, start, endFieldName, end, setIndexUiState, dateField]
  );

  const wrapperClasses = horizontal
    ? classnames('flex', 'w-full', 'justify-start', 'items-center')
    : '';

  const inputFieldClasses = horizontal
    ? classnames('flex', 'w-full', 'justify-start', 'items-center', 'mr-2')
    : classnames('mb-4', 'flex', 'w-full', 'justify-between', 'items-center');

  const inputLabelClasses = horizontal
    ? classnames('heading-2xs', 'text-gray-brightGray', 'font-semibold', 'mr-4')
    : classnames('heading-2xs', 'text-gray-brightGray', 'font-semibold');

  return (
    <div>
      {label && <div className={classnames('text-blue-navy', 'heading-sm', 'mb-6')}>{label}</div>}
      <div className={wrapperClasses}>
        <div className={inputFieldClasses}>
          <span className={inputLabelClasses}>{dict.fromDate}</span>
          <span>
            <DatePicker
              className={classnames(
                'border',
                'border-gray-spunPearl',
                'date-picker-icon',
                'rounded-md',
                'p-2',
                'w-full'
              )}
              placeholderText={dict.selectDate}
              selected={start}
              minDate={min}
              maxDate={end}
              onChange={(date) => handleStateChange({ [startFieldName]: date || undefined })}
            />
          </span>
        </div>
        <div className={inputFieldClasses}>
          <span className={inputLabelClasses}>{dict.toDate}</span>
          <span>
            <DatePicker
              className={classnames(
                'border',
                'border-gray-spunPearl',
                'date-picker-icon',
                'rounded-md',
                'p-2',
                'w-full'
              )}
              placeholderText={dict.selectDate}
              selected={end}
              minDate={start}
              maxDate={max}
              onChange={(date) => handleStateChange({ [endFieldName]: date || undefined })}
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFacet;
